import { useState, useRef, useEffect, useLayoutEffect } from 'react';

/**
 * Hook para gerenciar comportamentos de modal em tela cheia para dispositivos móveis
 * Lida com bloqueio de scroll, estados de renderização, e ajustes de estilo
 * 
 * @param {Object} options - Opções de configuração
 * @param {boolean} options.isOpen - Estado de abertura do modal
 * @param {boolean} options.isMobile - Se o dispositivo é móvel
 * @returns {Object} - Handlers, refs e estados para o modal
 */
export const useModalControls = ({ isOpen, isMobile = false }) => {
  // Refs para gerenciar elementos e estados de renderização
  const modalRef = useRef(null);
  const contentRef = useRef(null);
  const modalRenderIdRef = useRef(`modal-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`);
  const scrollBlockerRef = useRef(null);
  const originalScrollYRef = useRef(0);
  
  // Estados para controle de UI
  const [activeScreen, setActiveScreen] = useState('details');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Detecta iOS para ajustes específicos
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  // Capturar scrollY ANTES DE QUALQUER RENDERIZAÇÃO
  useLayoutEffect(() => {
    if (isOpen) {
      originalScrollYRef.current = window.scrollY;
      
      // Criar bloqueador de scroll IMEDIATAMENTE
      if (!scrollBlockerRef.current) {
        const blocker = document.createElement('div');
        blocker.id = 'modal-hard-scroll-blocker';
        blocker.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 99989;
          pointer-events: none;
        `;
        document.body.appendChild(blocker);
        scrollBlockerRef.current = blocker;
      }
      
      // Definir uma variável CSS global para a posição de scroll original
      document.documentElement.style.setProperty('--original-scroll-y', `${originalScrollYRef.current}px`);
      
      // ADICIONAR PROTEÇÃO ANTES DE QUALQUER REAÇÃO DO REACT
      const preventAnyScroll = (e) => {
        // Obter o elemento target de maneira segura
        const target = e.target || e.touches?.[0]?.target;
        
        // Se não temos um alvo válido, bloquear o evento
        if (!target) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return false;
        }
        
        // Verificar se é um elemento que deve permitir scroll
        const isScrollable = 
          // O próprio elemento tem id modal-content
          target.id === 'modal-content' || 
          // O elemento tem data-content-scrollable
          target.getAttribute?.('data-content-scrollable') === 'true' ||
          // O elemento tem uma das classes de scroll
          (target.classList && (
            target.classList.contains('modal-content-scrollable') ||
            target.classList.contains('overflow-y-auto') ||
            target.classList.contains('overflow-auto')
          ));
          
        // Se o próprio elemento não é scrollable, tentar verificar parentes
        // usando closest se disponível
        const hasScrollableParent = 
          typeof target.closest === 'function' && (
            target.closest('.modal-content-scrollable') ||
            target.closest('.overflow-y-auto') ||
            target.closest('.overflow-auto') ||
            target.closest('[data-content-scrollable="true"]')
          );
          
        if (!isScrollable && !hasScrollableParent) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return false;
        }
      };
      
      // Interceptar imediatamente eventos de scroll no nível de documento
      window.addEventListener('scroll', preventAnyScroll, { passive: false, capture: true });
      document.addEventListener('touchmove', preventAnyScroll, { passive: false, capture: true });
      document.addEventListener('wheel', preventAnyScroll, { passive: false, capture: true });
      
      // Atualizar o scrollY forçadamente de volta para o valor original
      if (window.scrollY !== originalScrollYRef.current) {
        window.scrollTo(0, originalScrollYRef.current);
      }
      
      // MODIFICAR O DOM PARA PREVENIR SCROLL
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100%';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.classList.add('modal-super-open');
      
      // ADICIONAR CLASSE PARA PADDING-TOP 0 EM DISPOSITIVOS MÓVEIS
      if (isMobile) {
        document.body.classList.add('modal-mobile-no-padding');
      }
      
      // Retornar função de limpeza para este efeito
      return () => {
        window.removeEventListener('scroll', preventAnyScroll, { passive: false, capture: true });
        document.removeEventListener('touchmove', preventAnyScroll, { passive: false, capture: true });
        document.removeEventListener('wheel', preventAnyScroll, { passive: false, capture: true });
        
        if (scrollBlockerRef.current) {
          scrollBlockerRef.current.remove();
          scrollBlockerRef.current = null;
        }
        
        document.body.style.overflow = '';
        document.body.style.height = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.classList.remove('modal-super-open');
        
        // Remover classe de padding-top zero para dispositivos móveis
        if (isMobile) {
          document.body.classList.remove('modal-mobile-no-padding');
        }
        
        // Restaurar a posição de scroll original
        window.scrollTo(0, originalScrollYRef.current);
      };
    }
  }, [isOpen, isMobile]);
  
  // Log de montagem/desmontagem do componente
  useEffect(() => {
    const renderId = modalRenderIdRef.current;
    console.log(`[Modal-${renderId}] MONTADO - isOpen:${isOpen}, scrollY:${window.scrollY}, originalY:${originalScrollYRef.current}`);
    
    return () => {
      console.log(`[Modal-${renderId}] DESMONTADO - isOpen:${isOpen}, scrollY:${window.scrollY}, originalY:${originalScrollYRef.current}`);
    };
  }, [isOpen]);
  
  // Substituir múltiplos useEffects por um único para estilos CSS
  useEffect(() => {
    const renderId = modalRenderIdRef.current;
    
    if (!isOpen) return;
    
    console.log(`[Modal-${renderId}] CRIANDO estilos CSS`);
    
    // Estilo unificado para modal
    const style = document.createElement('style');
    style.id = 'modal-styles-unified';
    style.innerHTML = `
      body.modal-super-open {
        top: calc(-1 * var(--original-scroll-y)) !important;
        overflow: hidden !important;
        position: fixed !important;
        width: 100% !important;
        touch-action: none !important;
      }
      
      body.modal-mobile-no-padding {
        padding-top: 0 !important;
      }
      
      @media (max-width: 767px) {
        body.modal-super-open .fixed.bottom-0.left-0.right-0.z-10.md\\:hidden,
        body.modal-super-open header.sticky.top-0.z-10,
        body.modal-super-open header.fixed.inset-x-0.top-0.z-50,
        body.modal-super-open .fixed.right-4.bottom-20.md\\:hidden,
        body.modal-super-open .fixed[class*="right-"][class*="bottom-"].md\\:hidden {
          display: none !important;
        }
      }
      
      .modal-scroll-container {
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
      }
      
      .modal-content-scrollable {
        -webkit-overflow-scrolling: touch !important;
        overscroll-behavior: contain !important;
        touch-action: pan-x pan-y !important;
        overflow-y: auto !important;
      }
      
      .modal-content-scrollable * {
        touch-action: pan-x pan-y !important;
      }
    `;
    
    if (!document.getElementById('modal-styles-unified')) {
      document.head.appendChild(style);
      console.log(`[Modal-${renderId}] ESTILOS CSS adicionados`);
    }
    
    return () => {
      const styleEl = document.getElementById('modal-styles-unified');
      if (styleEl) {
        styleEl.remove();
        console.log(`[Modal-${renderId}] ESTILOS CSS removidos`);
      }
    };
  }, [isOpen]);
  
  // Rolar para o topo quando o modal abrir ou tela mudar
  useEffect(() => {
    if (isOpen && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [isOpen, activeScreen]);
  
  // Handler para fechar modal ou voltar para a tela de detalhes
  const handleClose = () => {
    if (activeScreen !== 'details') {
      setActiveScreen('details');
    }
  };
  
  // Resetar estados ao fechar
  const resetModalState = () => {
    setActiveScreen('details');
    setIsSubmitting(false);
    setErrorMessage('');
  };
  
  return {
    // Refs
    modalRef, 
    contentRef,
    modalRenderIdRef,
    
    // Estados
    activeScreen,
    setActiveScreen,
    isSubmitting,
    setIsSubmitting,
    errorMessage,
    setErrorMessage,
    
    // Utilidades
    resetModalState,
    handleClose,
    originalScrollY: originalScrollYRef.current,
    
    // Detecção de dispositivo
    isIOS
  };
};

export default useModalControls; 